import type { ReactElement } from 'react';

import { ARTICLE_TOKENS } from './design-tokens';

type ShareTarget = {
  label: string;
  href: (url: string, title: string) => string;
  icon: ReactElement;
};

const encode = encodeURIComponent;

const SHARE_TARGETS: ShareTarget[] = [
  {
    label: 'Share on X',
    href: (url, title) =>
      `https://twitter.com/intent/tweet?url=${encode(url)}&text=${encode(
        title
      )}`,
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="15"
        height="15"
        fill="currentColor"
        aria-hidden
      >
        <path d="M18.9 2H22l-7.6 8.7L23 22h-6.9l-5.4-7.1L4.5 22H1.3l8.1-9.3L1 2h7l4.9 6.5L18.9 2Zm-1.2 18h1.9L7.4 3.9H5.4L17.7 20Z" />
      </svg>
    )
  },
  {
    label: 'Share on Facebook',
    href: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encode(url)}`,
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="15"
        height="15"
        fill="currentColor"
        aria-hidden
      >
        <path d="M13.5 22v-8.2h2.8l.4-3.3h-3.2V8.4c0-.9.3-1.6 1.6-1.6h1.7V3.9c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.4H7.3v3.3h2.8V22h3.4Z" />
      </svg>
    )
  },
  {
    label: 'Share on WhatsApp',
    href: (url, title) =>
      `https://api.whatsapp.com/send?text=${encode(`${title} ${url}`)}`,
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="15"
        height="15"
        fill="currentColor"
        aria-hidden
      >
        <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3.1-.2 0-.3 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.6 1.1 2.7c.1.2 1.9 2.9 4.6 4 1.7.7 2.3.8 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3Z" />
      </svg>
    )
  },
  {
    label: 'Share on LinkedIn',
    href: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encode(url)}`,
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="15"
        height="15"
        fill="currentColor"
        aria-hidden
      >
        <path d="M6.9 21H3.2V8.7h3.7V21ZM5 7A2.1 2.1 0 1 1 5 2.8 2.1 2.1 0 0 1 5 7Zm16 14h-3.7v-6c0-1.4 0-3.3-2-3.3s-2.3 1.6-2.3 3.2V21H9.3V8.7h3.6v1.7c.5-.9 1.7-1.9 3.5-1.9 3.8 0 4.5 2.5 4.5 5.7V21Z" />
      </svg>
    )
  }
];

export const ShareRow = ({ url, title }: { url: string; title: string }) => (
  <div
    className={`mt-14 flex items-center gap-3 border-t pt-7 ${ARTICLE_TOKENS.rule}`}
  >
    <span
      className={`mr-1 font-body tabular-nums text-[11px] uppercase tracking-[0.1em] ${ARTICLE_TOKENS.muted}`}
    >
      Share
    </span>
    {SHARE_TARGETS.map((target) => (
      <a
        key={target.label}
        href={target.href(url, title)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={target.label}
        className={`grid h-[34px] w-[34px] place-items-center rounded-lg border ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.bodyMuted} hover:border-[#14171c] hover:text-[#14171c]`}
      >
        {target.icon}
      </a>
    ))}
  </div>
);
